import { CheckOutlined, CloseOutlined, HolderOutlined, SettingOutlined } from '@ant-design/icons';
import { type MenuProps, Dropdown } from 'antd';
import i18n from 'i18next';
import { type ReactNode, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Spinner, Tag } from 'components/ui';

import type { WidgetConfig, WidgetConfigOption, WidgetDataState } from '../types';
import {
  type WidgetSizePresetId,
  WIDGET_SIZE_PRESET_IDS,
  WIDGET_SIZE_PRESETS,
} from '../widgetSizePresets';

import { WidgetErrorBoundary } from './WidgetErrorBoundary';

const FrameCard = styled.div<{ $isEditing: boolean }>`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 10px;
  background: ${({ theme }) => theme.secondaryBackgroundColor};
  border: 1px solid ${({ theme }) => theme.borderColor};
  box-shadow:
    0 1px 2px ${({ theme }) => theme.darkShadow},
    0 2px 6px ${({ theme }) => theme.darkShadow};
  transition:
    box-shadow 0.18s ease,
    border-color 0.18s ease;

  ${({ $isEditing, theme }) =>
    $isEditing
      ? `
        border-color: ${theme.teal};
        box-shadow: 0 0 0 1px ${theme.teal};
      `
      : `
        &:hover {
          border-color: ${theme.teal};
        }
      `}
`;

const Header = styled.div<{ $isEditing: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  padding: 9px 12px 9px 14px;
  border-bottom: 1px solid ${({ theme }) => theme.borderColor};
  ${({ $isEditing, theme }) =>
    $isEditing &&
    `
      background: ${theme.tealBackgroundColor};
      cursor: move;
      user-select: none;
    `}
`;

const Accent = styled.span`
  width: 3px;
  height: 14px;
  border-radius: 2px;
  flex: 0 0 auto;
  background: ${({ theme }) => theme.teal};
`;

const DragHandle = styled.span`
  cursor: move;
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.teal};
`;

const HeaderTitleGroup = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HeaderTitle = styled.span`
  min-width: 0;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: ${({ theme }) => theme.headerColor};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DataStateTag = styled(Tag)`
  flex: 0 0 auto;
  margin-inline-end: 0;
`;

const HeaderActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex: 0 0 auto;
`;

const IconButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  padding: 3px;
  border-radius: 4px;
  color: ${({ theme }) => theme.labelColor};
  transition:
    color 0.15s ease,
    background 0.15s ease;
  &:hover {
    color: ${({ theme }) => theme.headerColor};
    background: ${({ theme }) => theme.borderColor};
  }
`;

/* Non-scrolling box, so the dimming overlay covers a scrolled body. */
const ContentArea = styled.div<{ $isEditing: boolean }>`
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;

  /* Dim the body while customizing. An overlay, not opacity/filter, so charts
     aren't re-filtered on every drag/resize repaint. */
  ${({ $isEditing, theme }) =>
    $isEditing &&
    `
      &::after {
        content: '';
        position: absolute;
        inset: 0;
        background: ${theme.secondaryBackgroundColor};
        opacity: 0.6;
        pointer-events: none;
      }
    `}
`;

const Content = styled.div<{ $isEditing: boolean }>`
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;

  /* While customizing, the widget body is inert so in-widget tooltips/popups and
     hover states don't fire mid drag/resize (and never swallow grid interactions). */
  ${({ $isEditing }) => $isEditing && 'pointer-events: none;'}

  /* Keep table headers in view when a widget body scrolls. Scoped to the dashboard
     frame, so shared tables used elsewhere are untouched. */
  .ant-table-thead > tr > th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: ${({ theme }) => theme.secondaryBackgroundColor};
  }
`;

const FallbackWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

type WidgetFrameProps = {
  instanceId: string;
  widgetId: string;
  title: string;
  dataState: WidgetDataState;
  isEditing: boolean;
  /** Widget renders its own title chrome; the frame omits its header in view mode. */
  selfChrome: boolean;
  currentSize?: WidgetSizePresetId;
  configOptions?: WidgetConfigOption[];
  config?: WidgetConfig;
  onRemove: () => void;
  onSizeChange: (sizeId: WidgetSizePresetId) => void;
  onConfigChange: (config: WidgetConfig) => void;
  children: ReactNode;
};

// A teal check on the active size/config. antd's own selected tint is ~10% opacity
// and unreadable in dark mode, so this explicit, theme-coloured mark is the real
// "what's selected" cue. Always occupies the icon slot (transparent when inactive)
// so labels stay aligned.
const MenuCheck = styled(CheckOutlined)<{ $active: boolean }>`
  color: ${({ theme }) => theme.teal};
  opacity: ${({ $active }) => ($active ? 1 : 0)};
`;

function buildGearMenu({
  currentSize,
  configOptions,
  config,
  onSizeChange,
  onConfigChange,
  closeMenu,
}: Pick<
  WidgetFrameProps,
  'currentSize' | 'configOptions' | 'config' | 'onSizeChange' | 'onConfigChange'
> & { closeMenu: () => void }): MenuProps {
  const items: MenuProps['items'] = [
    {
      key: 'size-group',
      type: 'group',
      label: i18n.t('table.size', 'Size'),
      children: WIDGET_SIZE_PRESET_IDS.map(sizeId => ({
        key: `size:${sizeId}`,
        label: WIDGET_SIZE_PRESETS[sizeId].label,
        icon: <MenuCheck $active={currentSize === sizeId} />,
      })),
    },
  ];

  for (const option of configOptions ?? []) {
    items.push({ type: 'divider' });
    items.push({
      key: `group:${option.key}`,
      type: 'group',
      label: option.label,
      children: option.options.map(choice => ({
        key: `cfg:${option.key}:${choice.value}`,
        label: choice.label,
        icon: (
          <MenuCheck $active={(config?.[option.key] ?? option.defaultValue) === choice.value} />
        ),
      })),
    });
  }

  const selectedKeys: string[] = [];
  if (currentSize) {
    selectedKeys.push(`size:${currentSize}`);
  }
  for (const option of configOptions ?? []) {
    const value = config?.[option.key] ?? option.defaultValue;
    selectedKeys.push(`cfg:${option.key}:${value}`);
  }

  return {
    items,
    selectable: true,
    multiple: true,
    selectedKeys,
    onClick: ({ key, domEvent }) => {
      domEvent.stopPropagation();
      if (typeof key !== 'string') {
        return;
      }
      if (key.startsWith('size:')) {
        onSizeChange(key.slice('size:'.length) as WidgetSizePresetId);
        closeMenu();
        return;
      }
      if (key.startsWith('cfg:')) {
        const rest = key.slice('cfg:'.length);
        const sep = rest.indexOf(':');
        const optionKey = rest.slice(0, sep);
        const rawValue = rest.slice(sep + 1);
        const option = (configOptions ?? []).find(o => o.key === optionKey);
        const choice = option?.options.find(o => String(o.value) === rawValue);
        if (option && choice) {
          onConfigChange({ [optionKey]: choice.value });
        }
      }
    },
  };
}

/**
 * The visual shell for every dashboard tile: a uniform, elevated, theme-aware card
 * with a teal-accented header. In edit mode the header is the drag handle and
 * carries a gear menu (pick a size + per-widget config) and a remove button. Self-
 * chrome widgets hide the header in view mode so the dashboard never double-titles.
 */
export function WidgetFrame({
  instanceId,
  widgetId,
  title,
  dataState,
  isEditing,
  selfChrome,
  currentSize,
  configOptions,
  config,
  onRemove,
  onSizeChange,
  onConfigChange,
  children,
}: WidgetFrameProps) {
  const { t } = useTranslation();
  const showHeader = isEditing || !selfChrome;
  const [gearOpen, setGearOpen] = useState(false);

  return (
    <FrameCard $isEditing={isEditing} data-testid={`dashboard-widget-${widgetId}`}>
      {showHeader && (
        <Header $isEditing={isEditing} className={isEditing ? 'widget-drag-handle' : undefined}>
          {isEditing ? (
            <DragHandle aria-label={t('components.moveWidget', 'Move {{title}}', { title })}>
              <HolderOutlined />
            </DragHandle>
          ) : (
            <Accent />
          )}
          <HeaderTitleGroup>
            <HeaderTitle>{title}</HeaderTitle>
            {dataState === 'preview' && (
              <DataStateTag>{t('components.sample', 'Sample')}</DataStateTag>
            )}
          </HeaderTitleGroup>
          {isEditing && (
            <HeaderActions className="widget-no-drag">
              <Dropdown
                open={gearOpen}
                onOpenChange={setGearOpen}
                trigger={['click']}
                placement="bottomRight"
                menu={buildGearMenu({
                  currentSize,
                  configOptions,
                  config,
                  onSizeChange,
                  onConfigChange,
                  // Applying a size resizes/relocates the tile; close the menu so it
                  // can't be left detached from the moved widget (configs don't move
                  // the tile, so they keep the menu open to show the new selection).
                  closeMenu: () => setGearOpen(false),
                })}
              >
                <IconButton
                  type="button"
                  aria-label={t('components.configureWidget', 'Configure {{title}}', { title })}
                  data-testid={`dashboard-widget-gear-${instanceId}`}
                  onMouseDown={event => event.stopPropagation()}
                >
                  <SettingOutlined />
                </IconButton>
              </Dropdown>
              <IconButton
                type="button"
                aria-label={t('components.removeWidget', 'Remove {{title}}', { title })}
                data-testid={`dashboard-widget-remove-${instanceId}`}
                onMouseDown={event => event.stopPropagation()}
                onClick={onRemove}
              >
                <CloseOutlined />
              </IconButton>
            </HeaderActions>
          )}
        </Header>
      )}
      <ContentArea $isEditing={isEditing}>
        <Content $isEditing={isEditing}>
          <WidgetErrorBoundary>
            <Suspense
              fallback={
                <FallbackWrapper>
                  <Spinner />
                </FallbackWrapper>
              }
            >
              {children}
            </Suspense>
          </WidgetErrorBoundary>
        </Content>
      </ContentArea>
    </FrameCard>
  );
}
